// Extracted from original/src/App.tsx:44805-44875 (the "Exchange Rate Modal", `showRateModal`), opened from the
// Dealing in Dollar rates card (DollarVaultView.tsx:582-593). Kept: every class string, the backdrop / scale motion,
// icons and sizes, the `t.buyRate` / `t.sellRate` / `t.save` labels and the 1560 / 1570 placeholders.
// Cut: `handleUpdateRate` (App.tsx:17937-17981: role-name guard, `parseNumeric`, `localDataService.saveExchangeRate`,
// `handleSecurityCheck`) → `onSubmit` (the container: `PUT /rates` with `settings_currencies` + own-or-supervisor PIN,
// D-126, D-018). Changed by decision (F-240): the note no longer says the rates are "saved locally" — they are stored
// in the office's rate table with history and audit (D-126); the old text described the per-browser storage the new
// system removes. Added: data-testid attributes, `busy` and `error`.
import { motion, AnimatePresence } from 'motion/react';
import { TrendingUp, X } from 'lucide-react';
import { NumericInput } from '../components/NumericInput.js';
import { useT } from '../i18n-context.js';

export interface RateModalProps {
  readonly open: boolean;
  readonly buy: string;
  readonly sell: string;
  readonly onBuyChange: (value: string) => void;
  readonly onSellChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly busy?: boolean;
  readonly error?: string | null;
}

export function RateModal(props: RateModalProps) {
  const t = useT();
  return (
    <AnimatePresence>
      {props.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rate-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={props.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-900 dark:bg-[#05070a] text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight dark:digital-glow">
                <TrendingUp className="text-blue-400" />
                {t('dollar.rate_modal_title')}
              </h3>
              <button type="button" onClick={props.onClose} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!props.busy) props.onSubmit();
              }}
              className="p-6 space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div data-testid="rate-buy">
                  <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('buyRate')}</label>
                  <NumericInput
                    required
                    value={props.buy}
                    onChange={props.onBuyChange}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono dark:digital-glow transition-all"
                    placeholder="1560"
                  />
                </div>
                <div data-testid="rate-sell">
                  <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('sellRate')}</label>
                  <NumericInput
                    required
                    value={props.sell}
                    onChange={props.onSellChange}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono dark:digital-glow transition-all"
                    placeholder="1570"
                  />
                </div>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-2xl text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30">{t('dollar.rate_modal_note')}</div>
              {props.error && <div className="text-xs font-bold text-rose-600 dark:text-rose-400" data-testid="rate-error">{props.error}</div>}
              <button
                type="submit"
                data-testid="rate-save"
                disabled={props.busy}
                className="w-full py-4 bg-gray-900 dark:bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg hover:bg-gray-800 dark:hover:bg-blue-700 transition-all shadow-blue-600/20 disabled:opacity-50"
              >
                {t('save')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
